import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useShallow } from 'zustand/react/shallow';

import {
  DEFAULT_ROUTE_TYPE,
  DONATION_ROUTES,
  LOCAL_MARKET_ROUTES,
  ROUTE_TYPES,
  ROUTES,
  STORAGE_KEY,
} from '@/constants';
import { useGlobalStore } from '@/stores/GlobalStore';
import { useHistoryStore } from '@/stores/HistoryStore/useHistoryStore';

const setHistoryExpire = () => {
  sessionStorage.setItem(
    STORAGE_KEY.HISTORY_EXPIRE,
    (Date.now() + 5_000).toString(),
  );
};

const HistoryObserverController = () => {
  const searchParams = useSearchParams()?.toString();
  const pathname = usePathname();

  let url = pathname || '';

  if (searchParams) url += `?${searchParams}`;

  const [setCustomHistory, setHistoryIndex] = useHistoryStore(
    useShallow((state) => [state.setCustomHistory, state.setHistoryIndex]),
  );
  const setCurrentRouteType = useGlobalStore(
    (state) => state.setCurrentRouteType,
  );

  useEffect(() => {
    const { historyIndex, customHistory } = useHistoryStore.getState();

    const historyState: {
      historyIndex?: number;
    } = history.state ?? {};

    if (typeof historyState.historyIndex === 'number') {
      customHistory.set(historyState.historyIndex, url);
    } else {
      const hasReplaceMark =
        sessionStorage.getItem(STORAGE_KEY.REPLACE_MARK) === 'true';

      historyState.historyIndex = historyIndex;

      if (hasReplaceMark) {
        sessionStorage.removeItem(STORAGE_KEY.REPLACE_MARK);
        customHistory.set(historyIndex, url);
      } else {
        historyState.historyIndex += 1;
        customHistory.set(historyState.historyIndex, url);
      }

      history.replaceState(historyState, '');
    }

    //* 현재 라우팅 타입 갱신
    for (let i = historyState.historyIndex; i >= 0; i--) {
      const history = customHistory.get(i);
      if (!history) {
        if (i === 0) setCurrentRouteType(DEFAULT_ROUTE_TYPE);
        continue;
      }

      const historyPathname = history.split('?')[0];

      //* 라우팅 타입별 공용 페이지 제외
      if (
        historyPathname.startsWith(ROUTES.LOGIN.pathname) ||
        historyPathname.startsWith(ROUTES.MY.pathname) ||
        historyPathname.startsWith(ROUTES.JOIN_COMMON.pathname) ||
        historyPathname.startsWith(ROUTES.JOIN_COMPLETE.pathname) ||
        historyPathname.startsWith(ROUTES.EASY_CERTIFICATION.pathname)
      ) {
        if (i === 0) setCurrentRouteType(DEFAULT_ROUTE_TYPE);
        continue;
      }

      //* 고향사랑기부 라우팅일 경우 갱신
      if (historyPathname.startsWith(DONATION_ROUTES.HOME.pathname)) {
        setCurrentRouteType(ROUTE_TYPES.DONATION);

        break;
      }

      //* 웰로켓마켓 라우팅일 경우 갱신

      if (historyPathname.startsWith(LOCAL_MARKET_ROUTES.HOME.pathname)) {
        setCurrentRouteType(ROUTE_TYPES.LOCAL_MARKET);

        break;
      }

      //* 현재로서 나머지는 웰로
      setCurrentRouteType(ROUTE_TYPES.WELLO);
      break;
    }

    setHistoryIndex(historyState.historyIndex);
    setCustomHistory(customHistory);

    // pagehide 사용: beforeunload는 Safari bfcache를 비활성화시킴
    window.addEventListener('pagehide', setHistoryExpire);

    return () => {
      window.removeEventListener('pagehide', setHistoryExpire);
    };
  }, [url, setCustomHistory, setHistoryIndex, setCurrentRouteType]);

  return null;
};

export const HistoryObserver = () => {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense>
        <HistoryObserverController />
      </Suspense>
    </ErrorBoundary>
  );
};
